import type { ReactNode } from 'react';
import { Drafts } from '@/components/Icons/Drafts';
import { Incoming } from '@/components/Icons/Incoming';
import { Sent } from '@/components/Icons/Sent';
import { Star } from '@/components/Icons/Star';
import { Trash } from '@/components/Icons/Trash';
import { Unread } from '@/components/Icons/Unread';
import { MailFolderActionType, MailFolderType } from '@/types/components';
import type { MailFolder } from '@/types/mail';

export interface MailFolderConfig {
  showFollowUp: boolean;
  showReplyForward: boolean;
  repliesToFirstRecipient: boolean;
  showRecipientStatus: boolean;
  nameFromRecipient: boolean;
  opensCompose: boolean;
}

export const MAIL_FOLDER_CONFIG: Record<MailFolderType, MailFolderConfig> = {
  [MailFolderType.Inbox]: {
    showFollowUp: true,
    showReplyForward: true,
    repliesToFirstRecipient: false,
    showRecipientStatus: false,
    nameFromRecipient: false,
    opensCompose: false,
  },
  [MailFolderType.Unread]: {
    showFollowUp: true,
    showReplyForward: true,
    repliesToFirstRecipient: false,
    showRecipientStatus: false,
    nameFromRecipient: false,
    opensCompose: false,
  },
  [MailFolderType.FollowUp]: {
    showFollowUp: true,
    showReplyForward: true,
    repliesToFirstRecipient: false,
    showRecipientStatus: false,
    nameFromRecipient: false,
    opensCompose: false,
  },
  [MailFolderType.Deleted]: {
    showFollowUp: false,
    showReplyForward: false,
    repliesToFirstRecipient: false,
    showRecipientStatus: false,
    nameFromRecipient: false,
    opensCompose: false,
  },
  [MailFolderType.Drafts]: {
    showFollowUp: false,
    showReplyForward: false,
    repliesToFirstRecipient: false,
    showRecipientStatus: false,
    nameFromRecipient: true,
    opensCompose: true,
  },
  [MailFolderType.Sent]: {
    showFollowUp: false,
    showReplyForward: true,
    repliesToFirstRecipient: true,
    showRecipientStatus: true,
    nameFromRecipient: true,
    opensCompose: false,
  },
};

export const getMailFolderActions = (folder: MailFolder) => {
  const hasAction = (type: MailFolderActionType) =>
    folder.actions.some((action) => action.type === type);

  const canDelete = hasAction(MailFolderActionType.Delete);
  const canMarkAsRead = hasAction(MailFolderActionType.MarkAsRead);
  const canRestore = hasAction(MailFolderActionType.Refresh);
  const canDeletePermanently = hasAction(MailFolderActionType.PermanentDelete);

  return {
    canDelete,
    canMarkAsRead,
    canRestore,
    canDeletePermanently,
    canSelect: canDelete || canMarkAsRead || canRestore || canDeletePermanently,
    canMarkAllAsRead: hasAction(MailFolderActionType.MarkAllAsRead),
  };
};

export const mailFolderIcons: Record<MailFolderType, ReactNode> = {
  [MailFolderType.Inbox]: (
    <Incoming className="size-4 shrink-0 relative -top-px" />
  ),
  [MailFolderType.Unread]: <Unread className="size-4 shrink-0" />,
  [MailFolderType.FollowUp]: <Star className="size-4 shrink-0" />,
  [MailFolderType.Deleted]: <Trash className="size-4 shrink-0" />,
  [MailFolderType.Drafts]: <Drafts className="size-4 shrink-0" />,
  [MailFolderType.Sent]: <Sent className="size-4 shrink-0" />,
};
